import { useFormik } from "formik";
import { Button } from "primereact/button";
import * as Yup from "yup";
import { useTranslation } from "@i18n/hooks";
import { type EditAccountFormProps, type EditAccountFormState } from "./edit-account-form.types";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { ValidatedField } from "../../../../components/ValidatedField";
import { WarningUnsavedChangesDialog } from "../../../../components/WarningUnsavedChangesDialog";
import { useCallbackPrompt } from "../../../../hooks/UseCallBackPrompt";
import {
	addEditedFormToEditedFormsArray,
	resetArrayOfEditedForms,
	selectArrayOfEditedForms,
} from "../../../../store/slices/CoreSlice";
import { useTypedDispatch, useTypedSelector } from "../../../../store/store.redux";

export const EditAccountForm = ({ loading, initialValues, onSubmit }: EditAccountFormProps) => {
	const { t } = useTranslation(["common", "validation"]);

	const dispatch = useTypedDispatch();
	const arrayOfEditedForms = useTypedSelector(selectArrayOfEditedForms);
	const isEditing = arrayOfEditedForms.length > 0;
	const [showPrompt, confirmNavigation, cancelNavigation, setShowPrompt] =
		useCallbackPrompt(isEditing);

	const formId = "EditAccountForm";
	const formik = useFormik<EditAccountFormState>({
		initialValues: {
			name: initialValues?.name,
		},
		validationSchema: Yup.object().shape({
			name: Yup.string().required(t("validation:required")),
		}),
		onSubmit: (values, { setSubmitting }) => {
			onSubmit({
				...values,
			});
			setSubmitting(false);
			dispatch(resetArrayOfEditedForms());
		},
	});

	return (
		<>
			{showPrompt && (
				<WarningUnsavedChangesDialog
					confirmNavigation={confirmNavigation}
					setShowDialog={setShowPrompt}
					cancelNavigation={cancelNavigation}
				/>
			)}
			<form onSubmit={formik.handleSubmit} className="p-fluid">
				<ValidatedField<EditAccountFormState, string>
					name="name"
					label={t("common:accounts.accountNameLabel")}
					placeholder={t("common:accounts.accountNamePlaceholder")}
					component={DefaultTextFieldComponent}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					formikConfig={formik}
				/>
				<Button
					disabled={loading ?? !isEditing}
					type="submit"
					label={t("common:actions.save")}
				/>
			</form>
		</>
	);
};
